import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";

export type ReadyNotification = {
  /** The browser can notify and the user has not blocked it. */
  available: boolean;
  /** The user asked to be told. */
  asked: boolean;
  ask: () => void;
};

const supported = () => typeof window !== "undefined" && typeof (window as any).Notification === "function";

/**
 * "Notify me when it's ready" (owner, 2026-10-18 onboarding plan): a browser notification when the clip's result
 * lands, for a user who asked, gave permission, and is not looking at the page. Browser notifications only.
 */
export function useReadyNotification(ready: boolean): ReadyNotification {
  const [asked, setAsked] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(() =>
    supported() ? (window as any).Notification.permission : null
  );
  const wasReady = useRef(ready);

  useEffect(() => {
    const was = wasReady.current;
    wasReady.current = ready;
    if (was || !ready || !asked || !supported()) return;
    if ((window as any).Notification.permission !== "granted" || !document.hidden) return;
    try {
      const n = new (window as any).Notification("Your swing analysis is ready", {
        body: "Your key positions and coaching are in. Tap to see them.",
        tag: "eleetz-ready",
      });
      n.onclick = () => {
        window.focus();
        n.close?.();
      };
    } catch {
      /* some browsers only notify from a service worker: the page shows the result anyway */
    }
  }, [ready, asked]);

  const ask = () => {
    if (!supported()) return;
    setAsked(true);
    Promise.resolve((window as any).Notification.requestPermission())
      .then((p: NotificationPermission) => setPermission(p))
      .catch(() => undefined);
  };

  return { available: supported() && permission !== "denied", asked, ask };
}

/** The processing card's button, then its promise. */
export function NotifyWhenReady({ notify }: { notify: ReadyNotification }) {
  if (!notify.available) return null;
  return (
    <div className="mt-3 text-xs" data-testid="notify-when-ready">
      {notify.asked ? (
        <span className="text-slate-500">We'll let you know here when it's ready, even if you switch tabs.</span>
      ) : (
        <button type="button" className="inline-flex items-center gap-1.5 text-el-700 underline" onClick={notify.ask}>
          <Bell className="h-3.5 w-3.5" />
          Notify me when it's ready
        </button>
      )}
    </div>
  );
}
